import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { useTranslate } from "react-redux-multilingual";

// Nombre minimal de caractères (hors espaces de bord) avant de lancer la recherche
export const CLIENT_SEARCH_MIN_LENGTH = 3;
// Délai de debounce après la dernière frappe (ms)
const SEARCH_DEBOUNCE = 300;
// Plafond de résultats demandés au serveur
const SEARCH_LIMIT = 50;

/**
 * Hook partagé de recherche client côté serveur (endpoint api/client/name/).
 * La recherche porte sur le terme complet saisi (debounce, espaces de bord ignorés) ; le serveur renvoie
 * les meilleurs résultats plafonnés ainsi que le total, ce qui évite de charger et d'afficher toute la table.
 *
 * @param {Function} [onError] - Callback appelé si la requête échoue (hors fin de session)
 * @returns {Object} { datas, total, loading, footer, onInput, reset }
 *  - datas : [{ id, name }] prêt pour <InpDataList datas={datas} />
 *  - footer : texte "N / total - Précisez votre recherche" si la liste est tronquée, sinon false
 *  - onInput(value) : à appeler à chaque saisie ; renvoie true si la liste doit être affichée
 *  - reset() : vide la liste et annule toute recherche en cours
 */
const useClientSearch = (onError) => {
    const t = useTranslate();
    const [datas, setDatas] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const debounceTimer = useRef(null);
    // Identifiant de la dernière requête : permet d'ignorer les réponses arrivées en retard
    const lastRequestId = useRef(0);
    // Toujours la dernière version du callback, sans relancer les effets
    const onErrorRef = useRef(onError);
    onErrorRef.current = onError;

    /**
     * Interroge l'API avec le terme complet et met à jour la liste affichée.
     */
    const search = async (term) => {
        const requestId = ++lastRequestId.current;
        setLoading(true);
        try {
            const response = await axios.get(process.env.REACT_APP_API_URL + "api/client/name/" + encodeURIComponent(term) + "?limit=" + SEARCH_LIMIT);
            if (requestId !== lastRequestId.current) return;
            const list = response.data.datas.map((value) => ({
                id: value.id,
                name: value.name + ' ' + value.firstname + ' - ' + value.email + ' - ' + value.tel
            }));
            setDatas(list);
            setTotal(response.data.total ?? list.length);
            setLoading(false);
        } catch (error) {
            if (error == 'end_session') return;
            if (requestId !== lastRequestId.current) return;
            setLoading(false);
            if (onErrorRef.current) onErrorRef.current(error);
        }
    };

    /**
     * Vide la liste et invalide toute requête ou debounce en attente.
     */
    const reset = () => {
        clearTimeout(debounceTimer.current);
        lastRequestId.current++;
        setDatas([]);
        setTotal(0);
        setLoading(false);
    };

    /**
     * À appeler à chaque frappe. Renvoie true si la saisie est assez longue (liste à afficher).
     */
    const onInput = (value) => {
        clearTimeout(debounceTimer.current);
        const term = String(value ?? '').trim();
        if (term.length >= CLIENT_SEARCH_MIN_LENGTH) {
            debounceTimer.current = setTimeout(() => search(term), SEARCH_DEBOUNCE);
            return true;
        }
        reset();
        return false;
    };

    // Annule le debounce et invalide les requêtes en cours au démontage
    useEffect(() => {
        return () => {
            clearTimeout(debounceTimer.current);
            lastRequestId.current++;
        };
    }, []);

    const footer = (total > datas.length) ? datas.length + ' / ' + total + ' - ' + t('refineSearchResults') : false;

    return { datas, total, loading, footer, onInput, reset };
};

export default useClientSearch;
